<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useAuthStore } from '@state/stores/auth'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useSearchStore } from '@state/stores/search'
import { useAnalytics } from '@composables/use-analytics'
import { trialDays } from './../configuration'
import { isMac } from '../utils/user-agent'

const authStore = useAuthStore()
const searchStore = useSearchStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const route = useRoute()
const router = useRouter()
const analyticsService = useAnalytics()
const keyControlsService = useKeyControlsService()
const searchInput = ref(null)
const isPanelActive = ref(false)
const searchKeyword = ref('')
const loggedIn = computed(() => authStore.loggedIn)
const member = computed(() => ({ ...memberStore.currentMember }))
const isPremium = computed(() => premiumStore.isPremium)
const entitledFreeTrial = computed(() => !premiumStore.hasEverBeenPremium)
const routeKeyword = computed(() => route.params?.keyword)
const searchPlaceholder = 'Search music'
// Resolved after mount rather than during render: the platform is not knowable on a
// server, and a hint that appears once correct beats one that flips Ctrl to ⌘.
const modifierKey = ref('')

function focusSearchInput() {
    searchInput.value?.focus()
}

function setPanelInfo(value) {
    isPanelActive.value = value
}

function onPremiumUpsellClick() {
    analyticsService.premiumUpsellInitiated('top-bar')
}

function onSearch(keyword) {
    if (!keyword?.trim()) return

    const trimmedKeyword = keyword.trim()
    searchStore.updateKeyword(trimmedKeyword)
    router.push({
        name: 'search/results/top',
        params: { keyword: trimmedKeyword.replace(/\s+/g, '_') },
    })
    nextTick(focusSearchInput)
}

function onClearSearch() {
    searchKeyword.value = ''
}

watch(routeKeyword, keyword => {
    if (!keyword) return

    const routeValue = keyword.replace(/_/g, ' ')
    if (searchKeyword.value.trim() !== routeValue.trim()) {
        searchKeyword.value = routeValue
        nextTick(focusSearchInput)
    }
    searchStore.updateKeyword(routeValue)
}, { immediate: true })

onMounted(() => {
    modifierKey.value = isMac() ? '⌘' : 'Ctrl'
    keyControlsService.on('focusSearch', focusSearchInput)
})
onBeforeUnmount(() => keyControlsService.off('focusSearch', focusSearchInput))
</script>

<template>
    <div
        v-if="$route.name !== 'login'"
        class="header-wrapper"
    >
        <!-- Center: Search Bar -->
        <div class="search-bar-wrapper">
            <BaseSearchInput
                ref="searchInput"
                v-model="searchKeyword"
                :placeholder="searchPlaceholder"
                variant="header"
                @search="onSearch"
                @clear="onClearSearch"
            >
                <template #append="{ clear, hasInputValue }">
                    <BaseIcon
                        v-if="hasInputValue"
                        class="clear-icon"
                        name="close"
                        @click="clear"
                    />
                    <div
                        v-else
                        class="keyboard-shortcut"
                    >
                        <span
                            v-if="modifierKey"
                            class="key-badge"
                        >{{ modifierKey }}</span>
                        <span class="key-badge">S</span>
                    </div>
                </template>
            </BaseSearchInput>
        </div>

        <!-- Right: Auth buttons OR User section -->
        <div class="right-section">
            <!-- Non-authenticated: Sign Up / Log in -->
            <div
                v-if="!loggedIn"
                class="auth-buttons"
            >
                <BaseLink name="signup">
                    <BaseButton
                        :modifiers="{ noMargins: true }"
                        button-type="transparentWhite"
                        @click="() => {}"
                    >Sign Up</BaseButton>
                </BaseLink>
                <BaseLink
                    class="login-button"
                    name="login"
                >
                    <BaseButton
                        :modifiers="{ noMargins: true }"
                        button-type="tagBlack"
                        @click="() => {}"
                    >Log in</BaseButton>
                </BaseLink>
            </div>

            <!-- Authenticated: User section -->
            <div
                v-else
                class="user-details"
            >
                <BaseLink
                    v-if="!isPremium"
                    :to="{ name: 'premium/view-plans', query: { from: 'topbar' } }"
                    class="premium-link"
                >
                    <BaseButton
                        button-type="highlight"
                        size="xsmall"
                        :modifiers="{ fill: true, noMargins: true }"
                        @click="onPremiumUpsellClick"
                    >{{ entitledFreeTrial ? `START ${trialDays}-DAY FREE TRIAL` : `UPGRADE TO PREMIUM` }}</BaseButton>
                </BaseLink>
                <div
                    v-else
                    class="premium-link"
                >
                    <div class="premium-container">
                        <BaseIcon
                            name="crown"
                            filled
                            class="icon-premium"
                        />
                        <span class="premium-text">PREMIUM</span>
                    </div>
                </div>
                <div class="icon-container">
                    <BaseResourceImage
                        v-if="member.avatar"
                        class="image"
                        :should-lazyload="false"
                        :resource="member"
                        :fill="false"
                        :centered="true"
                        type="avatar"
                    />
                    <BaseIcon
                        v-else
                        class="icon-user"
                        name="user"
                    />
                </div>
                <BaseDropdown
                    class="button"
                    :padded="false"
                    @is-panel-active="setPanelInfo"
                >
                    <template #trigger>
                        <div>
                            <BaseIcon
                                :class="['icon-dropdown', { active: isPanelActive }]"
                                name="caret-down"
                            />
                        </div>
                    </template>
                    <template #dropdown>
                        <div
                            class="user-menu"
                        >
                            <ul class="user-menu-list">
                                <li class="user-menu-item">
                                    <BaseLink
                                        class="user-menu-link"
                                        name="settings"
                                    >Settings</BaseLink>
                                </li>
                                <li class="user-menu-item">
                                    <BaseLink
                                        class="user-menu-link"
                                        name="account/overview"
                                        target="_blank"
                                    >Account Management</BaseLink>
                                </li>
                                <div class="user-menu-separator" />
                                <li class="user-menu-item">
                                    <BaseLink
                                        class="user-menu-link"
                                        name="logout"
                                    >Log out</BaseLink>
                                </li>
                            </ul>
                        </div>
                    </template>
                </BaseDropdown>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

$dropdown-item-height: 44px;
$dropdown-color: $c-text-tertiary;
$avatar-size: 30px;

.header-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.search-bar-wrapper {
    display: none;
    flex: 1;
    align-items: center;
    max-width: 600px;
    height: $size-touch-target;
    padding: 0 $s-l;
    margin: 0;
    background: $c-header-searchbar-bg;
    border-radius: $radius-full;

    @include desktop {
        display: flex;
    }
}



.clear-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-left: $s-m;
    color: $c-white;
    cursor: pointer;
    opacity: 0.7;

    @include transition(opacity);

    &:hover {
        opacity: 1;
    }
}

.keyboard-shortcut {
    display: flex;
    gap: $s-xxs;
    align-items: center;
    margin-left: $s-m;
}

.key-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    font-size: 12px;
    font-weight: 500;
    color: $c-header-searchbar-placeholder;
    border: 1px solid $c-header-searchbar-placeholder;
    border-radius: $radius-5;
}

.right-section {
    display: flex;
    flex-shrink: 0;
    align-items: center;
}

.auth-buttons {
    display: flex;
    align-items: center;
}

.user-details {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0 -5px;
}

.premium-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.premium-text {
    @extend %contentSubtitleS;

    margin-top: 1px;
    font-size: 12px;
}

.image {
    width: $avatar-size;
    height: $avatar-size;
    overflow: hidden;
    border-radius: 50%;
}

.icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $avatar-size;
    height: $avatar-size;
    overflow: hidden;
    text-align: center;
    border-radius: 50%;
}

.icon-user {
    position: relative;
    display: block;
    width: $avatar-size;
    height: $avatar-size;
    padding: 5px;
    color: $c-black;
    background: $c-white;
}

.icon-premium {
    width: 27px;
    height: 27px;
    color: $c-accent-default;
}

.icon-dropdown {
    width: 34px;
    height: 34px;
    color: $c-white;

    &.active {
        transform: rotate(180deg);
    }
}

.login-button {
    margin-left: $s-m;
}

.user-menu {
    text-align: center;
    &-list {
        padding: 0;
        margin: 0;
        list-style: none;
    }
    &-item {
        width: 100%;
        height: $dropdown-item-height;
    }
}

.user-menu-link {
    @extend %contentSubtitleL;

    position: relative;
    display: block;
    line-height: $dropdown-item-height;
    color: $dropdown-color;

    &::before {
        @include fill;

        z-index: $layer-negative-z-index;
        content: '';
        background: $c-state-hover;
        opacity: 0;

        @include transition(opacity);
    }

    &:hover {
        &::before {
            opacity: 1;
        }
    }

    &:hover,
    &:active {
        text-decoration: none;
    }

    &:visited {
        color: $dropdown-color;
    }
}

.user-menu-separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    background: $c-border-default;
}

.premium-link {
    margin-right: $s-m;

    &:hover {
        text-decoration: none;
    }
}
</style>
